/* eslint-disable react-hooks/exhaustive-deps */
import { useImperativeHandle } from "react"
import type { RefObject } from "react"

import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isNumber from "lodash-es/isNumber"

import { EModalId, useModalDispatcher } from "@/3514/store/modal"
import type { TEmptyCallback } from "@/shared/types/functions"

import {
    useCoachingChatConversationIdSelector as useConversationIdSelector,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector,
    useCoachingChatFormSelector as useFormSelector
} from "../store"
import {
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatEntityMode as EEntityMode,
    type TCoachingChatEntityRef as TEntityRef,
    type _TCoachingChatHookSharedParams as _TSharedParams
} from "../types"

type _TParams = _TSharedParams & { entityRef: RefObject<TEntityRef> }

function useAbandoner({ entityId, entityMode, wasChatCompleted, entityRef }: _TParams): void {
    const modalDispatcher: ReturnType<typeof useModalDispatcher> = useModalDispatcher()

    const formState: ReturnType<typeof useFormSelector> = useFormSelector(entityId)

    const conversationId: number | null = useConversationIdSelector(entityId)

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const shouldRunBasedOnStatus: boolean = includes<EConversationStatus>(
        [
            EConversationStatus.Connected,
            EConversationStatus.Created,
            EConversationStatus.Paused,
            EConversationStatus.Ready,
            EConversationStatus.InProgress
        ],
        conversationStatus
    )

    const isLightMode: boolean = eq(entityMode, EEntityMode.Light)

    useImperativeHandle<TEntityRef, TEntityRef>(
        entityRef,
        (): TEntityRef => ({
            openAbandonmentModal: (onConfirm: TEmptyCallback): void => {
                if (isLightMode || wasChatCompleted || !shouldRunBasedOnStatus) onConfirm()
                else modalDispatcher.showModal(EModalId.Participant_CoachingChatAbandonment, { onConfirm })
            },
            areSeedQuestionsCompleted: isNumber(conversationId) ? true : formState.isSubmitted
        }),
        [conversationId, shouldRunBasedOnStatus, formState.isSubmitted, isLightMode, wasChatCompleted]
    )
}

export { useAbandoner as useCoachingChatAbandoner }
